/**
 * What the guest asked for, as chips on the reservation row.
 *
 * The booking form collects children by age band, a seating preference, a
 * requested table and a company — and for a while it collected all of that and
 * showed it to nobody. A form that asks whether you need a high chair and then
 * hides the answer from the person setting the table is worse than a form that
 * never asked.
 *
 * Chips rather than a detail panel, because the people who need this are
 * reading a list during service and will not open a row to find it. Same reason
 * allergies already ride with the guest's name.
 *
 * Renders nothing when the guest asked for nothing, which is most bookings.
 */

import { useTranslation } from 'react-i18next';
import type { ReservationRow } from '../lib/api.js';

export function GuestRequests({ booking }: { booking: ReservationRow }) {
  const { t } = useTranslation();

  const kids = booking.kids_0_2 + booking.kids_3_6 + booking.kids_7_12;
  const chips: { key: string; text: string; tone: string }[] = [];

  if (kids > 0) {
    // Ages, not just a count. Two under-twos need high chairs; two twelve-year
    // olds need chairs. "2 children" cannot tell anyone which.
    const parts = [
      booking.kids_0_2 ? t('requests.kids02', { count: booking.kids_0_2 }) : null,
      booking.kids_3_6 ? t('requests.kids36', { count: booking.kids_3_6 }) : null,
      booking.kids_7_12 ? t('requests.kids712', { count: booking.kids_7_12 }) : null,
    ].filter(Boolean);
    chips.push({
      key: 'kids',
      text: parts.join(' · '),
      tone: 'bg-amber-50 text-amber-800',
    });
  }

  if (booking.seating_preference) {
    chips.push({
      key: 'seating',
      // Stored as an untranslated key so reports can group it; translated here
      // for the person reading it.
      text: t(`requests.seating_${booking.seating_preference}`, {
        defaultValue: booking.seating_preference,
      }),
      tone: 'bg-brand-teal-light text-brand-teal-dark',
    });
  }

  if (booking.preferred_table) {
    chips.push({
      key: 'table',
      text: t('requests.wantsTable', { table: booking.preferred_table }),
      tone: 'bg-slate-100 text-slate-700',
    });
  }

  if (booking.company_name) {
    const pct = Number(booking.corporate_discount_pct ?? 0);
    chips.push({
      key: 'company',
      // The discount is shown only when one actually applied. A company name
      // with no contract is still worth seeing — it is who the booking is for —
      // but showing "0%" beside it would read as a rate rather than as absence.
      text: pct > 0
        ? t('requests.companyWithDiscount', { company: booking.company_name, pct })
        : booking.company_name,
      tone: 'bg-brand-bronze-light text-brand-bronze-dark',
    });
  }

  if (chips.length === 0) return null;

  return (
    <div className="mt-1 flex flex-wrap gap-1">
      {chips.map((chip) => (
        <span key={chip.key} className={`rounded px-1.5 py-0.5 text-xs ${chip.tone}`}>
          {chip.text}
        </span>
      ))}
    </div>
  );
}
